import { useStores, type WebhookEndpoint } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import CopyButton from '@/components/blocks/CopyButton';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { useWebhookEndpointActions } from '@/features/routines/hooks/use-webhook-endpoint-actions';
import { useWebhookEndpoints } from '@/features/routines/use-routine-refresh';

import { SettingsSection } from '../settings-section';

type WebhookEndpointActions = ReturnType<typeof useWebhookEndpointActions>;

const fieldsSummary = (endpoint: WebhookEndpoint) => {
  if (endpoint.payloadFields === null) return 'No sample payload';
  const count = endpoint.payloadFields.length;
  return `${count} ${count === 1 ? 'field' : 'fields'} from a sample payload`;
};

const endpointSummary = (endpoint: WebhookEndpoint) =>
  [
    endpoint.hasSecret ? null : 'Needs a secret',
    endpoint.teamId ? 'Shared with your team' : null,
    fieldsSummary(endpoint),
  ]
    .filter(Boolean)
    .join(' · ');

export const WebhooksSettings = observer(function WebhooksSettings() {
  const { routines } = useStores();
  const endpoints = useWebhookEndpoints();
  const actions = useWebhookEndpointActions({ context: 'WebhooksSettings' });
  const isLoading =
    endpoints.length === 0 && routines.isOperationPending('webhook-endpoints');

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Webhooks</SettingsSection.Title>
        <SettingsSection.Description>
          URLs that other services post to. Your routines can start on a post to
          your endpoints or your team's.
        </SettingsSection.Description>
      </SettingsSection.Header>

      <SettingsSection.Group
        title="Endpoints"
        trailing={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void actions.create()}
          >
            <Icons.plus className="size-3.5" />
            New endpoint
          </Button>
        }
      >
        {isLoading ? (
          <XStack className="items-center gap-2 text-foreground-muted">
            <Icons.loader className="size-4 animate-spin" />
            <span className="text-sm">Loading endpoints...</span>
          </XStack>
        ) : endpoints.length > 0 ? (
          endpoints.map((endpoint, index) => (
            <WebhookEndpointRow
              key={endpoint.id}
              endpoint={endpoint}
              actions={actions}
              separated={index > 0}
            />
          ))
        ) : (
          <p className="text-sm text-foreground-muted">
            No webhook endpoints yet. Create one here, or from a routine's
            webhook trigger.
          </p>
        )}
      </SettingsSection.Group>
    </SettingsSection.Root>
  );
});

function WebhookEndpointRow({
  endpoint,
  actions,
  separated,
}: {
  endpoint: WebhookEndpoint;
  actions: WebhookEndpointActions;
  separated: boolean;
}) {
  return (
    <>
      {separated ? <SettingsSection.Seperator /> : null}
      <SettingsSection.Item className="w-full flex-col items-stretch gap-2">
        <SettingsSection.ItemHeader>
          <SettingsSection.ItemLabel>{endpoint.name}</SettingsSection.ItemLabel>
          {endpoint.description ? (
            <SettingsSection.ItemDescription>
              {endpoint.description}
            </SettingsSection.ItemDescription>
          ) : null}
          <SettingsSection.ItemDescription>
            {endpointSummary(endpoint)}
          </SettingsSection.ItemDescription>
        </SettingsSection.ItemHeader>
        <XStack className="min-w-0 items-center gap-1">
          <code className="min-w-0 flex-1 truncate font-mono text-xs text-foreground-muted">
            {endpoint.url}
          </code>
          <CopyButton
            text={endpoint.url}
            copyText="Copy webhook URL"
            size="sm"
            aria-label={`Copy URL for ${endpoint.name}`}
          />
        </XStack>
        {endpoint.canManage ? (
          <YStack className="items-end">
            <XStack className="flex-wrap justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit ${endpoint.name}`}
                onClick={() => void actions.edit(endpoint)}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void actions.regenerateSecret(endpoint.id)}
              >
                {endpoint.hasSecret ? 'Regenerate secret' : 'Generate secret'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void actions.enterSenderSecret(endpoint)}
              >
                Use sender&apos;s secret
              </Button>
              <Button
                variant="destructive-ghost"
                size="sm"
                aria-label={`Delete ${endpoint.name}`}
                onClick={() => void actions.remove(endpoint)}
              >
                Delete
              </Button>
            </XStack>
          </YStack>
        ) : null}
      </SettingsSection.Item>
    </>
  );
}
